/**
 * Модуль для покупки существ
 */
import { logger, isFunction, safeCall } from '../../utils/logger.js';

/**
 * Показывает диалог подтверждения
 */
function showConfirm(message, telegramWebApp) {
  return new Promise((resolve) => {
    if (telegramWebApp && isFunction(telegramWebApp.showConfirm)) {
      try {
        telegramWebApp.showConfirm(message, (result) => resolve(result === true));
      } catch (error) {
        logger.warn("showConfirm error:", error);
        resolve(window.confirm ? window.confirm(message) : true);
      }
    } else {
      resolve(window.confirm ? window.confirm(message) : true);
    }
  });
}

/**
 * Создаёт менеджер для покупки существ
 */
export function createCreaturePurchaseManager({
  safeFetch,
  buildAuthHeaders,
  getApiUrl,
  getTelegramWebApp,
  getCurrentUser,
  setCurrentUser,
  updateUserBalancesUI,
  showScreen,
  showCreatureDetails,
  loadStoreScreen,
  escapeHtmlFn,
}) {
  let isBuying = false;
  // Используем переменную для хранения функции, чтобы можно было обновить её позже
  let showCreatureDetailsFn = showCreatureDetails;

  /**
   * Покупает существо
   */
  async function buyCreature(creatureId) {
    logger.debug(`[buyCreature] Starting purchase for creature ${creatureId}`);
    const currentUser = getCurrentUser();
    if (!currentUser) {
      logger.error(`[buyCreature] No current user`);
      const telegramWebApp = getTelegramWebApp();
      if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert("Ошибка: пользователь не авторизован.");
      }
      return;
    }

    if (isBuying) {
      logger.debug("Purchase already in progress, skipping...");
      return;
    }

    isBuying = true;
    const telegramWebApp = getTelegramWebApp();

    try {
      const apiUrl = getApiUrl();
      const authHeaders = buildAuthHeaders();
      
      // Проверяем, что заголовки авторизации есть
      if (!authHeaders || Object.keys(authHeaders).length === 0) {
        logger.error(`[buyCreature] No auth headers!`);
        if (telegramWebApp?.showAlert) {
          telegramWebApp.showAlert("Ошибка авторизации. Попробуйте перезагрузить приложение.");
        }
        return;
      }
      
      logger.debug(`[buyCreature] Making request to ${apiUrl}/buy-creature`);
      
      const response = await safeFetch(`${apiUrl}/buy-creature`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify({ creatureId }),
      });

      logger.debug(`[buyCreature] Response received`, { success: response.success });

      if (!response.success) {
        throw new Error(response.error || "Ошибка покупки");
      }

      const data = response.data;
      if (data.success) {
        // Обновляем данные пользователя
        const updatedUser = { ...currentUser, ...data.userData };
        setCurrentUser(updatedUser);
        updateUserBalancesUI(updatedUser);

        let message = "Существо добавлено в коллекцию! 🎉";
        if (data.royalty && data.royalty.coins > 0) {
          message += `\n\n${data.royalty.message}`;
        }
        if (data.professionBonus) {
          message += `\n\n✨ Бонус профессии Искажатель изображения: +${data.professionBonus.bonus} монет (${data.professionBonus.bonusPercent}%)`;
        }

        if (telegramWebApp?.showAlert) {
          telegramWebApp.showAlert(message);
        }

        showScreen("store");
        if (loadStoreScreen) {
          loadStoreScreen();
        }
      } else {
        if (data.error === "not_enough_stars") {
          const creature = data.creature || {};
          const priceStars = creature.priceStars || 0;
          const confirmed = await showConfirm(
            `Недостаточно звёзд для покупки.\nДля покупки требуется ${priceStars} ⭐.\nУ вас: ${currentUser.stars} ⭐.\nХотите приобрести звёзды?`,
            telegramWebApp,
          );

          if (confirmed) {
            if (showPaymentScreen) {
              showPaymentScreen();
            } else {
              showScreen("payment");
            }
          } else {
            if (telegramWebApp?.showAlert) {
              telegramWebApp.showAlert("Покупка отменена: не хватает ⭐");
            }
          }
        } else if (data.error === "creature_already_owned") {
          if (telegramWebApp?.showAlert) {
            telegramWebApp.showAlert(
              data.message || "У вас уже есть это существо в коллекции"
            );
          }
          // Переходим в коллекцию, чтобы показать существо
          showScreen("collection");
        } else {
          if (telegramWebApp?.showAlert) {
            telegramWebApp.showAlert(
              data.message || data.error || "Ошибка покупки",
            );
          }
        }
        showScreen("store");
        if (loadStoreScreen) {
          loadStoreScreen();
        }
      }
    } catch (error) {
      logger.error("Buy creature error:", error);
      if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert(
          error.message || error.error || "Ошибка при покупке существа",
        );
      }
    } finally {
      isBuying = false;
    }
  }

  /**
   * Показывает подтверждение покупки и покупает существо
   */
  async function showBuyConfirm(creature) {
    if (!creature) {
      logger.error(`[showBuyConfirm] No creature provided`);
      return;
    }

    const creatureId = creature.id || creature.creatureId;
    if (!creatureId) {
      logger.error(`[showBuyConfirm] No creatureId found`);
      return;
    }

    const priceStars = creature.priceStars ?? creature.price_stars ?? 0;
    const name = creature.name || creature.creature_name || `Глитчлинг #${creatureId}`;
    const message = priceStars
      ? `Купить ${escapeHtmlFn(name)} за ${priceStars} ⭐?`
      : `Купить ${escapeHtmlFn(name)}?`;

    logger.debug(`[showBuyConfirm] Showing confirmation dialog`, { creatureId, priceStars });
    const telegramWebApp = getTelegramWebApp();
    const confirmed = await showConfirm(message, telegramWebApp);

    if (confirmed) {
      logger.debug(`[showBuyConfirm] User confirmed, calling buyCreature`);
      await buyCreature(creatureId);
    } else {
      logger.debug(`[showBuyConfirm] User cancelled purchase`);
    }
  }

  /**
   * Показывает детали существа из магазина
   */
  function showCreatureDetailsFromStore(creature) {
    if (!creature) {
      logger.warn('[showCreatureDetailsFromStore] No creature provided');
      return;
    }

    logger.debug('[showCreatureDetailsFromStore] Called', {
      creatureId: creature.id || creature.creatureId,
      name: creature.name
    });

    const storeItem = {
      ...creature,
      isStoreItem: true,
      creature_id: creature.id || creature.creatureId,
      creature_name: creature.name,
      creature_image: creature.imageUrl || creature.image_url,
      base_attributes: creature.baseAttributes || creature.base_attributes || {},
      price_stars: creature.priceStars || creature.price_stars || 0,
      priceStars: creature.priceStars || creature.price_stars || 0,
    };

    if (isFunction(showCreatureDetailsFn)) {
      logger.debug('[showCreatureDetailsFromStore] Calling showCreatureDetails');
      showCreatureDetailsFn(storeItem);
    } else {
      logger.error('[showCreatureDetailsFromStore] showCreatureDetails not available');
      // Fallback: показываем alert с информацией о существе
      const telegramWebApp = getTelegramWebApp();
      if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert(`Существо: ${storeItem.creature_name}\nЦена: ${storeItem.priceStars} ⭐`);
      }
    }
  }
  
  // Функция для обновления showCreatureDetails после инициализации
  function setShowCreatureDetails(fn) {
    showCreatureDetailsFn = fn;
  }

  return {
    buyCreature,
    showBuyConfirm,
    showCreatureDetailsFromStore,
    setShowCreatureDetails,
    showConfirm: (message) => showConfirm(message, getTelegramWebApp()),
  };
}

